/**
 * <video-section> Web Component (Optimized, no private methods)
 * Supports MP4, YouTube, Vimeo with lazy loading & autoplay
 */
class VideoSection extends HTMLElement {
    static get observedAttributes() { return ['data-video-url', 'data-video-url-mobile']; }
    static preconnectsAdded = false;
  
    constructor() {
      super();
      this.videoLoaded = false;
    }
  
    connectedCallback() {
      if (!VideoSection.preconnectsAdded) this.addPreconnects();
      const isPriority = this.hasAttribute('data-priority') || this.isFirst();
      isPriority ? this.loadVideo(true) : this.observeVideo();
    }
  
    disconnectedCallback() { this.observer?.disconnect(); }
  
    attributeChangedCallback(name, oldVal, newVal) {
      if ((name === 'data-video-url' || name === 'data-video-url-mobile') && oldVal !== newVal && this.videoLoaded) {
        // In future we could support live reloading; for now we ignore to avoid flicker.
      }
    }
  
    // ---- Helper Methods ----
    isFirst() {
      return document.querySelector('video-section') === this;
    }
  
    addPreconnects() {
      ['https://www.youtube.com', 'https://i.ytimg.com', 'https://player.vimeo.com']
        .forEach(url => {
          if (!document.querySelector(`link[rel="preconnect"][href="${url}"]`)) {
            const link = Object.assign(document.createElement('link'), {
              rel: 'preconnect', href: url, crossOrigin: 'anonymous'
            });
            document.head.appendChild(link);
          }
        });
      VideoSection.preconnectsAdded = true;
    }
  
    observeVideo() {
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !this.videoLoaded) {
          this.loadVideo();
          this.observer.disconnect();
        }
      }, { rootMargin: '50px', threshold: 0.01 });
      this.observer.observe(this);
    }
  
    extractYouTubeID(url) {
      const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([^&?\/]+)/);
      return match ? match[1] : '';
    }

    hasInteractiveControls() {
      return (
        this.hasAttribute('data-controls') ||
        this.hasAttribute('data-player-ui') ||
        this.hasAttribute('data-no-autoplay')
      );
    }

    postYouTubeCommand(iframe, func, args = '') {
      if (!iframe?.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func, args }),
          '*'
        );
      } catch (e) {}
    }

    postVimeoCommand(iframe, method, value) {
      if (!iframe?.contentWindow) return;
      try {
        const payload =
          value !== undefined && value !== null ? { method, value } : { method };
        iframe.contentWindow.postMessage(JSON.stringify(payload), '*');
      } catch (e) {}
    }

    bindEmbedControls(iframe, type) {
      const toolbar = this.querySelector('.vid-EmbedControls');
      if (!toolbar || !iframe) return;

      const playBtn = toolbar.querySelector('[data-action="play-pause"]');
      const muteBtn = toolbar.querySelector('[data-action="mute"]');
      let isPlaying = true;
      let isMuted = true;

      const syncPlayLabel = () => {
        if (!playBtn) return;
        playBtn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        playBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
        playBtn.classList.toggle('is-paused', !isPlaying);
      };

      const syncMuteLabel = () => {
        if (!muteBtn) return;
        muteBtn.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
        muteBtn.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
        muteBtn.classList.toggle('is-muted', isMuted);
      };

      const sendMute = () => {
        if (type === 'youtube') this.postYouTubeCommand(iframe, 'mute');
        else this.postVimeoCommand(iframe, 'setVolume', 0);
      };

      const sendUnmute = () => {
        if (type === 'youtube') this.postYouTubeCommand(iframe, 'unMute');
        else this.postVimeoCommand(iframe, 'setVolume', 1);
      };

      playBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (type === 'youtube') {
          this.postYouTubeCommand(iframe, isPlaying ? 'pauseVideo' : 'playVideo');
        } else {
          this.postVimeoCommand(iframe, isPlaying ? 'pause' : 'play');
        }
        isPlaying = !isPlaying;
        syncPlayLabel();
      });

      muteBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isMuted) {
          sendUnmute();
          isMuted = false;
        } else {
          sendMute();
          isMuted = true;
        }
        syncMuteLabel();
      });

      syncPlayLabel();
      syncMuteLabel();
    }

    createPlayerControls() {
      const toolbar = document.createElement('div');
      toolbar.className = 'vid-EmbedControls';
      toolbar.setAttribute('role', 'toolbar');
      toolbar.setAttribute('aria-label', 'Video controls');
      toolbar.innerHTML = `
        <button type="button" class="vid-EmbedControls_Button" data-action="mute" aria-label="Unmute video" aria-pressed="true">
          <span class="vid-EmbedControls_Icon vid-EmbedControls_Icon--volume" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
              <line x1="22" y1="9" x2="16" y2="15"></line>
              <line x1="16" y1="9" x2="22" y2="15"></line>
            </svg>
          </span>
          <span class="vid-EmbedControls_Icon vid-EmbedControls_Icon--muted" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
            </svg>
          </span>
        </button>
      `;
      return toolbar;
    }
  
    createMP4Video(url, priority) {
      const context = this.dataset.videoContext || '';
      const isCarousel = context === 'video-carousel';
      const noAutoplay = this.hasAttribute('data-no-autoplay');
      const initHideControls = this.hasAttribute('data-init-hide-controls');
      const togglePlay = this.hasAttribute('data-toggle-play') || isCarousel;
      const autoplayRequested = this.hasAttribute('data-auto-play') || !noAutoplay;

      const video = Object.assign(document.createElement('video'), {
        src: url,
        autoplay: autoplayRequested,
        muted: true,
        playsInline: true,
        loop: true,
        controls: (this.hasAttribute('data-controls') || noAutoplay) && !initHideControls
      });

      video.style = 'width:100%;height:100%;object-fit:cover;display:block;';
      if (this.dataset.poster) video.poster = this.dataset.poster;
      if (this.dataset.videoClass) video.className = this.dataset.videoClass;
      
      // If controls should be hidden initially, show them on play
      if (initHideControls) {
        video.addEventListener('play', () => {
          video.controls = false;
          const playButton = this.querySelector('.video-play-button');
          if (playButton) playButton.style.display = 'none';
        });
        video.addEventListener('ended', () => {
          const playButton = this.querySelector('.video-play-button');
          if (playButton) playButton.style.display = 'flex';
        });
      }

      if (togglePlay) {
        video.style.cursor = 'pointer';
        video.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (video.paused) {
            // Ensure video is ready and attempt to play
            const attemptPlay = () => {
              video.muted = true;
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  // Playback started successfully
                }).catch((error) => {
                  console.log('Video play failed:', error);
                  // If play fails, check if we need to reload
                  if (video.readyState === 0 || video.readyState === 1) {
                    // Video not loaded or metadata only, try loading
                    video.load();
                    video.addEventListener('canplay', () => {
                      video.play().catch((err) => {
                        console.log('Video play failed after load:', err);
                      });
                    }, { once: true });
                  } else {
                    // Video is loaded but play failed - might be a policy issue
                    // Try again after a short delay
                    setTimeout(() => {
                      video.play().catch((err) => {
                        console.log('Video play retry failed:', err);
                      });
                    }, 100);
                  }
                });
              }
            };
            
            // Check video ready state
            if (video.readyState >= 2) {
              // Video has current data or more, can play
              attemptPlay();
            } else if (video.readyState === 1) {
              // Video has metadata, wait for more data
              video.addEventListener('canplay', attemptPlay, { once: true });
              // Trigger loading if needed
              if (video.networkState === 2) {
                video.load();
              }
            } else {
              // Video not loaded, load it first
              video.load();
              video.addEventListener('canplay', attemptPlay, { once: true });
            }
          } else {
            video.pause();
          }
        });
      }
      
      return video;
    }
  
    createIframe(url) {
      const noAutoplay = this.hasAttribute('data-no-autoplay');
      const interactive = this.hasInteractiveControls();
      const usePlayerUi = this.hasAttribute('data-player-ui');
      const showNativeControls = this.hasAttribute('data-controls') && !usePlayerUi;
      const iframe = Object.assign(document.createElement('iframe'), {
        width: '100%', height: '100%', frameBorder: '0',
        allow: 'autoplay; fullscreen; picture-in-picture',
        loading: 'lazy', allowFullscreen: true
      });
      iframe.style = `width:100%;height:100%;border:0;display:block;pointer-events:${interactive ? 'auto' : 'none'};`;
      if (this.dataset.iframeClass) iframe.className = this.dataset.iframeClass;

      let src = '';
      let embedType = '';
      if (/youtu/.test(url)) {
        embedType = 'youtube';
        const params = new URLSearchParams({
          enablejsapi: 1,
          autoplay: noAutoplay ? 0 : 1,
          mute: 1,
          controls: showNativeControls ? 1 : 0,
          rel: 0,
          playsinline: 1,
          modestbranding: 1,
          showinfo: 0
        });
        if (window.location?.origin) params.set('origin', window.location.origin);
        src = `https://www.youtube.com/embed/${this.extractYouTubeID(url)}?${params}`;
        if (!iframe.classList.contains('js-youtube')) iframe.classList.add('js-youtube');
      } else if (/vimeo/.test(url)) {
        embedType = 'vimeo';
        const id = url.split('/').pop();
        const params = new URLSearchParams({
          autoplay: noAutoplay ? 0 : 1,
          muted: 1,
          controls: showNativeControls ? 1 : 0,
          background: showNativeControls || usePlayerUi ? 0 : 1,
          playsinline: 1
        });
        src = `https://player.vimeo.com/video/${id}?${params}`;
        iframe.classList.add('js-vimeo');
      }
      iframe.src = src;
      iframe.dataset.embedType = embedType;
      return iframe;
    }
  
    getCurrentUrl() {
      const desktop = this.dataset.videoUrl?.trim();
      const mobile = this.dataset.videoUrlMobile?.trim();
      if (mobile && window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
        return mobile;
      }
      return desktop;
    }
  
    loadVideo(priority = false) {
      if (this.videoLoaded) return;
      const url = this.getCurrentUrl();
      if (!url) return console.log('No video URL');
  
      const baseUrl = url.split('?')[0] || '';
      const isMP4 = baseUrl.toLowerCase().endsWith('.mp4');
      const usePlayerUi = this.hasAttribute('data-player-ui') && !isMP4;

      this.innerHTML = '';
      this.classList.toggle('video-section--has-controls', usePlayerUi || this.hasInteractiveControls());

      if (usePlayerUi) {
        const shell = document.createElement('div');
        shell.className = 'vid-EmbedShell';
        shell.append(this.createPlayerControls());
        const element = this.createIframe(url);
        shell.append(element);
        this.append(shell);
        this.embedIframe = element;
        this.embedType = element.dataset.embedType || '';

        element.addEventListener('load', () => {
          const enforceMute = () => {
            if (this.embedType === 'youtube') this.postYouTubeCommand(element, 'mute');
            else if (this.embedType === 'vimeo') this.postVimeoCommand(element, 'setVolume', 0);
          };
          enforceMute();
          window.setTimeout(enforceMute, 400);
          this.bindEmbedControls(element, this.embedType);
        }, { once: true });
      } else {
        const element = isMP4 ? this.createMP4Video(url, priority) : this.createIframe(url);
        this.append(element);
        this.embedIframe = isMP4 ? null : element;
        this.embedType = isMP4 ? 'mp4' : element?.dataset?.embedType || '';

        if (!isMP4 && element) {
          element.addEventListener(
            'load',
            () => {
              if (this.embedType === 'youtube') this.postYouTubeCommand(element, 'mute');
            },
            { once: true }
          );
        }
      }

      const element = this.querySelector('video, iframe');
      this.videoLoaded = true;
      this.dataset.loaded = 'true';
      this.dispatchEvent(new CustomEvent('video-loaded', {
        detail: { url, type: isMP4 ? 'mp4' : 'iframe' }
      }));

      const noAutoplay = this.hasAttribute('data-no-autoplay');
      const autoplayRequested = this.hasAttribute('data-auto-play') || !noAutoplay || priority;

      if (isMP4 && element && autoplayRequested) {
        element.muted = true;
        element.play().catch(() => {});
      }
      
      // Set up play button click handler for MP4 videos
  if (isMP4) {
    const playButton = this.querySelector('.video-play-button');
    const element = this.querySelector('video');
  
    if (playButton && element) {
      playButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Ensure video is ready before playing
        const attemptPlay = () => {
          element.muted = true; // important for iOS autoplay
          const playPromise = element.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.log('Video play failed:', error);
              // If play fails, try loading the video first
              if (element.readyState < 2) {
                element.load();
                element.addEventListener('canplay', () => {
                  element.muted = true;
                  element.play().catch((err) => {
                    console.log('Video play failed after load:', err);
                  });
                }, { once: true });
              }
            });
          }
        };
        
        // Check if video is ready
        if (element.readyState >= 2) {
          attemptPlay();
        } else {
          element.load();
          element.addEventListener('canplay', attemptPlay, { once: true });
        }
      });
    }
  }
  
    }
  
    // ---- Public Methods ----
    reload() {
      this.videoLoaded = false;
      delete this.dataset.loaded;
      this.loadVideo(true);
    }

    pause() {
      const video = this.querySelector('video');
      if (video) {
        video.pause();
        return;
      }
      const iframe = this.embedIframe || this.querySelector('iframe');
      if (!iframe) return;
      if (/youtube/i.test(iframe.src || '')) this.postYouTubeCommand(iframe, 'pauseVideo');
      else if (/vimeo/i.test(iframe.src || '')) this.postVimeoCommand(iframe, 'pause');
    }

    play() {
      const video = this.querySelector('video');
      if (video) {
        video.muted = true;
        video.play().catch(() => {});
        return;
      }
      const iframe = this.embedIframe || this.querySelector('iframe');
      if (!iframe) return;
      if (/youtube/i.test(iframe.src || '')) {
        this.postYouTubeCommand(iframe, 'mute');
        this.postYouTubeCommand(iframe, 'playVideo');
      } else if (/vimeo/i.test(iframe.src || '')) {
        this.postVimeoCommand(iframe, 'play');
      }
    }
  }
  
  customElements.define('video-section', VideoSection);
